const GA_TRACKING_ID = 'G-1WMW9271MW';
const ANALYTICS_LOAD_DELAY = 8000;
const ROUTE_UPDATE_DELAY = 250;
const interactionEvents = [
  'pointerdown',
  'keydown',
  'touchstart',
  'scroll',
] as const;

type GtagCommand = Parameters<NonNullable<Window['gtag']>>;

let isInitialized = false;
let isScriptLoading = false;
let isConfigured = false;
let routeTimer: number | undefined;
let loadTimer: number | undefined;

const hasDoNotTrack = (): boolean => {
  const dnt =
    window.doNotTrack ||
    navigator.doNotTrack ||
    (navigator as Navigator & { msDoNotTrack?: string }).msDoNotTrack;

  return dnt === '1' || dnt === 'yes';
};

const gtag = (...args: GtagCommand): void => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(args);
};

const configureAnalytics = (): void => {
  if (isConfigured) return;

  window.gtag = window.gtag || gtag;
  window.gtag('js', new Date());
  window.gtag('config', GA_TRACKING_ID, {
    anonymize_ip: true,
    cookie_expires: 0,
    send_page_view: false,
  });

  isConfigured = true;
};

const loadAnalytics = (): void => {
  if (isScriptLoading || hasDoNotTrack()) return;

  isScriptLoading = true;
  window.clearTimeout(loadTimer);
  removeInteractionListeners();
  configureAnalytics();

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`;
  document.head.appendChild(script);
};

const removeInteractionListeners = (): void => {
  interactionEvents.forEach((eventName) => {
    window.removeEventListener(eventName, loadAnalytics);
  });
};

export const initAnalytics = (): void => {
  if (
    isInitialized ||
    process.env.NODE_ENV !== 'production' ||
    hasDoNotTrack()
  ) {
    return;
  }

  isInitialized = true;
  configureAnalytics();

  interactionEvents.forEach((eventName) => {
    window.addEventListener(eventName, loadAnalytics, {
      once: true,
      passive: true,
    });
  });

  loadTimer = window.setTimeout(loadAnalytics, ANALYTICS_LOAD_DELAY);
};

export const trackPageView = (location: Location): void => {
  if (!isInitialized || hasDoNotTrack()) return;

  window.clearTimeout(routeTimer);
  routeTimer = window.setTimeout(() => {
    window.gtag?.('event', 'page_view', {
      page_location: location.href,
      page_path: `${location.pathname}${location.search}${location.hash}`,
      page_title: document.title,
    });
  }, ROUTE_UPDATE_DELAY);
};

export const onAnalyticsLoadedForTest = (): boolean => isScriptLoading;

export const resetAnalyticsForTest = (): void => {
  isInitialized = false;
  isScriptLoading = false;
  isConfigured = false;
  window.clearTimeout(routeTimer);
  window.clearTimeout(loadTimer);
  removeInteractionListeners();
  routeTimer = undefined;
  loadTimer = undefined;
};
